import {
  Check,
  Star,
  Smartphone,
  Mail,
  Flame,
  LineChart,
  Target,
  MessageSquareText,
  Handshake,
  Award,
  Trophy,
  User,
  LucideIcon,
} from "lucide-react";

interface Row {
  Icon: LucideIcon;
  feature: string;
  quantity?: string;
  benefit: string;
  premium: boolean;
  elite: boolean;
  premiumFallback?: string; // shown when premium=false to explain the affiliate's responsibility
}

const ROWS: Row[] = [
  {
    Icon: Smartphone,
    feature: "Done-for-You Social Media",
    quantity: "20 custom posts every month",
    benefit:
      "Stay consistently visible, build trust, and remain top of mind without creating content yourself.",
    premium: true,
    elite: true,
  },
  {
    Icon: Mail,
    feature: "Done-for-You Lead Generation",
    quantity: "Up to 200–300 personalized outreach emails during Month 1",
    benefit:
      "Our team researches qualified prospects and consistently fills your pipeline with new opportunities.",
    premium: true,
    elite: true,
  },
  {
    Icon: MessageSquareText,
    feature: "Lead Nurturing & Follow-up",
    quantity: "Up to 100 personalized follow-up emails each month",
    benefit:
      "Stay connected with interested prospects while we help keep conversations moving forward.",
    premium: true,
    elite: true,
  },
  {
    Icon: Flame,
    feature: "Qualified Leads Delivered",
    quantity: "Qualified prospects delivered every month",
    benefit:
      "Spend less time searching for leads and more time building relationships.",
    premium: true,
    elite: true,
  },
  {
    Icon: LineChart,
    feature: "Monthly Growth Review",
    quantity: "1 strategy review every month",
    benefit:
      "Receive recommendations to continuously improve your lead generation and subscriber growth.",
    premium: true,
    elite: true,
  },
  {
    Icon: Target,
    feature: "Discovery Calls",
    quantity: "Up to 10 professionally conducted Discovery Calls every month",
    benefit:
      "Your dedicated Growth Advisor professionally presents the WinCampaign opportunity to qualified prospects on your behalf.",
    premium: false,
    elite: true,
    premiumFallback: "You conduct your own Discovery Calls.",
  },
  {
    Icon: Handshake,
    feature: "Dedicated Growth Advisor",
    quantity: "One dedicated Growth Advisor",
    benefit:
      "An extension of your business who helps move qualified prospects through the Discovery Call process.",
    premium: false,
    elite: true,
    premiumFallback: "You personally present the opportunity and follow up with your prospects.",
  },
];

function StatusBadge({
  included,
  elite,
  fallback,
}: {
  included: boolean;
  elite: boolean;
  fallback?: string;
}) {
  if (!included && fallback) {
    return (
      <div>
        <span className="inline-flex items-center gap-1.5 font-display font-semibold text-sm text-amber-800 bg-amber-100 rounded-full px-2.5 py-1">
          <User size={13} strokeWidth={2.5} /> You Do It Yourself
        </span>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">
          {fallback}
        </p>
      </div>
    );
  }
  if (!included) {
    return (
      <span className="inline-flex items-center gap-1.5 font-display font-semibold text-sm text-gray-400">
        —
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-display font-semibold text-sm rounded-full px-2.5 py-1 ${
        elite ? "bg-[#d3fc3a] text-[#1a1a24]" : "bg-[#673dfc] text-white"
      }`}
    >
      <Check size={13} strokeWidth={3} /> Done For You
    </span>
  );
}

export default function ComparisonTable() {
  return (
    <section
      data-testid="wc-comparison"
      className="bg-white py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-[52px] tracking-tight text-[#30265b] leading-[1.05]">
            Choose the level of support that gets you to your{" "}
            <span className="text-[#673dfc]">$10K/month recurring income faster.</span>
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Both plans help you grow your subscriber base. The difference is{" "}
            <span className="font-display font-semibold text-[#30265b]">
              how much of the work you want to do yourself
            </span>{" "}
            versus having the WinCampaign team help you every month.
          </p>
        </div>

        {/* Desktop: 3-column table */}
        <div className="mt-14 sm:mt-16 hidden lg:block">
          <div className="rounded-3xl border border-gray-200 overflow-visible bg-white shadow-sm">
            {/* Header row */}
            <div className="grid grid-cols-[1.6fr_1fr_1fr] bg-gradient-to-b from-[#f8f6ff] to-white rounded-t-3xl overflow-hidden">
              <div className="p-7 border-r border-gray-200">
                <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc]">
                  Compare your growth support
                </div>
              </div>
              <div className="p-7 border-r border-gray-200">
                <div className="flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#30265b]/70">
                  <Award size={14} strokeWidth={2} /> PREMIUM
                </div>
                <div className="mt-1 font-display font-bold text-lg text-[#30265b] leading-tight">
                  DFY Business Growth Accelerator PREMIUM
                </div>
                <div className="mt-2 text-sm text-gray-600 leading-snug">
                  Marketing + lead generation.
                </div>
              </div>
              <div className="relative p-7 pt-10 bg-[#30265b] text-white rounded-tr-3xl">
                {/* Centered "Fastest Path" badge — sits cleanly at the top of the ELITE cell */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#d3fc3a] text-[#1a1a24] text-[10px] font-display font-bold uppercase tracking-[0.14em] py-1.5 px-4 rounded-full shadow-lg shadow-[#673dfc]/30 flex items-center gap-1.5 whitespace-nowrap z-10">
                  <Star size={11} fill="#1a1a24" /> Fastest Path to $10K/Month
                </div>
                <div className="flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a]">
                  <Trophy size={14} strokeWidth={2} /> ELITE
                </div>
                <div className="mt-1 font-display font-bold text-lg leading-tight">
                  DFY Business Growth Accelerator ELITE
                </div>
                <div className="mt-2 text-sm text-white/80 leading-snug">
                  Marketing + lead generation + up to 10 Discovery Calls conducted for you.
                </div>
              </div>
            </div>

            {/* Rows */}
            {ROWS.map((r, i) => {
              const Icon = r.Icon;
              return (
                <div
                  key={r.feature}
                  className={`grid grid-cols-[1.6fr_1fr_1fr] ${
                    i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  } border-t border-gray-200`}
                >
                  <div className="p-6 sm:p-7 border-r border-gray-200 flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#f3efff] text-[#673dfc] grid place-items-center flex-shrink-0">
                      <Icon size={18} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-[#30265b] leading-tight">
                        {r.feature}
                      </div>
                      {r.quantity && (
                        <div className="mt-1 font-display font-semibold text-[#673dfc] text-sm leading-snug">
                          {r.quantity}
                        </div>
                      )}
                      <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                        {r.benefit}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 sm:p-7 border-r border-gray-200 flex items-start">
                    <StatusBadge included={r.premium} elite={false} fallback={r.premiumFallback} />
                  </div>
                  <div className="p-6 sm:p-7 bg-[#f8f5ff] border-l-2 border-[#c5b4ff]/40 flex items-start">
                    <StatusBadge included={r.elite} elite={true} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile / tablet: stacked cards */}
        <div className="mt-12 space-y-4 lg:hidden">
          {ROWS.map((r) => {
            const Icon = r.Icon;
            return (
              <div
                key={r.feature}
                className="rounded-3xl border border-gray-200 overflow-hidden bg-white"
              >
                <div className="p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#f3efff] text-[#673dfc] grid place-items-center flex-shrink-0">
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-[#30265b] leading-tight">
                      {r.feature}
                    </div>
                    {r.quantity && (
                      <div className="mt-1 font-display font-semibold text-[#673dfc] text-sm leading-snug">
                        {r.quantity}
                      </div>
                    )}
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                      {r.benefit}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 border-t border-gray-100">
                  <div className="p-4 border-r border-gray-100">
                    <div className="flex items-center gap-1.5 text-[10px] font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc] mb-2">
                      <Award size={11} strokeWidth={2} /> PREMIUM
                    </div>
                    <StatusBadge included={r.premium} elite={false} fallback={r.premiumFallback} />
                  </div>
                  <div className="p-4 bg-[#30265b] text-white">
                    <div className="flex items-center gap-1.5 text-[10px] font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a] mb-2">
                      <Trophy size={11} strokeWidth={2} /> ELITE
                    </div>
                    <StatusBadge included={r.elite} elite={true} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
